/**
 * Pure parsing + shaping helpers for the `find_shortest_path` tool result. No
 * React, no IO — the controller (FindShortestPathToolUI) and the presentational
 * view both consume these.
 */

import type { GraphEdge, GraphNode } from '@/components/neo4j/cytoscape/types'

export interface FindShortestPathArgs {
  source_node_id: string
  target_node_id: string
  max_depth?: number
}

export interface PathNode {
  element_id: string
  name: string
  labels: string[]
  /** Server-provided 1-line summary built from the entity's properties /
   * `_entity_summary`. Empty string when nothing was available. */
  subtitle?: string
}

export interface PathStep {
  node: PathNode
  relationship?: { element_id: string; type: string }
}

export interface FindShortestPathResult {
  status: 'ok' | 'empty' | 'error'
  source?: PathNode
  target?: PathNode
  path_length?: number
  steps?: PathStep[]
  graph?: { nodes: GraphNode[]; edges: GraphEdge[] }
  /** IDs only — the UI calls `/graph/hydrate` to materialize a full
   * `GraphView` when the user clicks "Show on graph" / "Save". Keeps the
   * agent SSE payload small (we don't ship every node property over the
   * stream). */
  hydrate?: { node_ids: string[]; relationship_ids: string[]; label?: string }
  error?: string
}

const INTERNAL = '__'

export function visibleLabels(labels: string[]): string[] {
  return labels.filter(l => !l.startsWith(INTERNAL))
}

export function primaryLabel(labels: string[]): string | undefined {
  return visibleLabels(labels)[0]
}

export function parseResult(
  raw: FindShortestPathResult | string | undefined
): FindShortestPathResult | null {
  if (raw == null) return null
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as FindShortestPathResult
    } catch {
      return { status: 'error', error: raw }
    }
  }
  return raw
}
